import { IImportLeadPayload, PaySource } from '../types/api';
import { GaEvent, GaTransaction } from '../types/ga';
import { IProduct } from '../types/product';
import { toFixedFloat } from '../utils/numbers';

import { Api, IMakeError } from './api';

export type GaPaymentButtonType =
  | string // paypal_{mid} | paypal_reach_{mid} | paypal_express_{mid}
  | 'klarna'
  | 'credit_card'
  | 'apple_pay'
  | 'apple_pay_express'
  | 'google_pay'
  | 'one_click'
  | 'klarna_stripe';

export class Gtm {
  private static logEvent = (event: GaEvent) => {
    if (window?.dataLayer) {
      Api.sendGtmDataToBigQuery({
        event: event?.event,
        data: event,
      });
      window.dataLayer.push(event);
    }
  };

  static trackErrorEvent = (error: IMakeError) => {
    Gtm.logEvent({
      event: 'fe_error',
      ...error,
    });
  };

  static trackBuyButtonClick = (
    email: string,
    orderId: string,
    paymentType: GaPaymentButtonType,
    auid?: string,
    sessionId?: string,
    mid?: string | number,
  ) => {
    Gtm.logEvent({
      event: 'payment_button_click',
      user_id: email,
      transaction_id: orderId,
      brand: window.BRAND,
      auid: auid || '',
      sessionId: sessionId || '',
      payment_type: paymentType || '',
      merchant_id: mid || '',
    });
  };

  static trackPageView = (category: string, productName: string) => {
    Gtm.logEvent({
      event: 'eec.checkoutStep',
      brand: window.BRAND,
      ecommerce: {
        checkout: {
          category,
          PageViewProduct: productName,
        },
      },
    });
  };

  static trackEmailPopupOpen = () => {
    Gtm.logEvent({
      event: 'email_popup_open',
      brand: window.BRAND,
    });
  };

  static trackPopupEmailSubmit = (email: string) => {
    Gtm.logEvent({
      event: 'email_popup_submit',
      brand: window.BRAND,
      user_id: email,
    });
  };

  static trackEmailPopupClose = (email?: string) => {
    Gtm.logEvent({
      event: 'email_popup_closed',
      brand: window.BRAND,
      user_id: email,
    });
  };

  static trackEmail = (email: string) => {
    Gtm.logEvent({
      event: 'Email_Event',
      user_id: email,
      brand: window.BRAND,
    });
  };

  static trackUpsellButtonClick = (
    paymentType: PaySource,
    orderId?: string,
    email?: string | number,
    auid?: string,
  ) => {
    Gtm.logEvent({
      event: `upsell_payment_button_click`,
      user_id: email,
      transaction_id: orderId,
      payment_type: paymentType,
      brand: window.BRAND,
      auid,
    });
  };

  static trackTransaction = (
    lead: IImportLeadPayload,
    product: IProduct,
    upsellKey?: number,
    products?: IProduct[],
  ) => {
    let category =
      typeof upsellKey === 'number' ? `Upsell${upsellKey + 1}` : 'Order';
    if (typeof upsellKey === 'number' && product?.isDownsell) {
      category = `Downsell${upsellKey + 1}`;
    }

    const isMultipleInitialProducts =
      Array.isArray(products) && products.length > 1;
    const gwId = isMultipleInitialProducts ? products[0]?.mid : product.mid;
    const shippingPrice = isMultipleInitialProducts
      ? products[0]?.shippingPrice
      : product.shippingPrice;

    const gtmProducts: any[] = [];
    const gtmActionField = {
      id: lead.orderId,
      tax: '',
      coupon: '',
      gw_id: gwId,
      email: lead.emailAddress,
      payment_type: lead.paymentType,
      shipping: shippingPrice,
      revenue: toFixedFloat(product.finalPrice + shippingPrice),
    };
    const gtmEvent = {
      event: 'eec.transaction',
      transaction_id: lead.orderId,
      user_id: lead.customerId,
      auid: lead?.auid,
      sessionId: lead?.sessionId,
      brand: window.BRAND,
      ecommerce: {
        currencyCode: product.currencyCode,
        purchase: {
          actionField: gtmActionField,
          products: gtmProducts,
        } as GaTransaction,
      },
    };

    if (isMultipleInitialProducts) {
      let totalPrice = 0;
      products.forEach(el => {
        totalPrice += Number(el.finalPrice);
        gtmActionField.revenue = toFixedFloat(totalPrice + shippingPrice);
        gtmProducts.push({
          id: String(el.id),
          name: el.name,
          brand: window.BRAND,
          price: String(el.finalPrice),
          quantity: el.quantity,
          category,
          variant: '',
          position: '',
        });
      });
    } else {
      gtmProducts.push({
        id: String(product.id),
        name: product.name,
        brand: window.BRAND,
        price: String(product.finalPrice),
        quantity: product.quantity,
        category,
        variant: '',
        position: '',
      });
    }

    Gtm.logEvent(gtmEvent);
  };
}
